<template>
  <a-drawer
    title="设置名医荣誉"
    :width="400"
    :visible="open"
    :mask-closable="false"
    @close="handleCancel"
  >
    <a-form :form="form" layout="vertical">
      <!-- 医师姓名展示 -->
      <a-form-item label="医师姓名">
        <span>{{ doctorInfo.name || '' }}</span>
      </a-form-item>

      <!-- 荣誉列表操作区域 -->
      <a-form-item
        label="名医荣誉列表"
        :validateStatus="validateStatus"
        :help="helpMsg"
      >
        <!-- 已添加的荣誉项 -->
        <div class="honor-list">
          <div v-for="(item, index) in honorList" :key="index" class="honor-item">
            <a-row :gutter="12" align="middle">
              <!-- 荣誉名称 -->
              <a-col :span="10">
                <a-input
                  v-model="item.honorName"
                  placeholder="请输入荣誉名称"
                  @blur="validateHonorItem(item, index)"
                  :status="item.nameError ? 'error' : ''"
                  maxlength="255"
                />
                <div v-if="item.nameError" class="error-tip">{{ item.nameError }}</div>
              </a-col>

              <!-- 荣誉类型 -->
              <a-col :span="8">
                <a-select
                  v-model="item.honorType"
                  placeholder="请选择类型"
                  @change="validateHonorItem(item, index)"
                  :status="item.typeError ? 'error' : ''"
                >
                  <a-select-option value="1">国家级</a-select-option>
                  <a-select-option value="2">省级</a-select-option>
                  <a-select-option value="3">市级</a-select-option>
                </a-select>
                <div v-if="item.typeError" class="error-tip">{{ item.typeError }}</div>
              </a-col>

              <!-- 删除按钮 -->
              <a-col :span="6" class="operate-col">
                <a-button
                  type="text"
                  danger
                  icon="delete"
                  @click="removeHonorItem(index)"
                >
                  删除
                </a-button>
              </a-col>
            </a-row>
          </div>
        </div>

        <!-- 添加荣誉项按钮 -->
        <a-button
          type="dashed"
          icon="plus"
          class="add-honor-btn"
          @click="addHonorItem"
        >
          添加荣誉项
        </a-button>
      </a-form-item>

      <!-- 当前已设置荣誉展示 -->
      <a-form-item label="当前已设置荣誉">
        <div v-if="doctorInfo.honorList && doctorInfo.honorList.length" class="current-honor">
          <div v-for="(honor, idx) in doctorInfo.honorList" :key="idx" class="current-honor-item">
            {{ honor.honorName }}（{{ getHonorTypeName(honor.honorType) }}）
          </div>
        </div>
        <div v-else class="empty-tip">未设置任何荣誉</div>
      </a-form-item>
    </a-form>

    <!-- 底部操作按钮（和名校设置保持一致） -->
    <div style="position: absolute; bottom: 20px; right: 20px; width: calc(100% - 40px); text-align: right;">
      <a-button
        style="margin-right: 8px"
        @click="handleCancel"
      >
        取消
      </a-button>
      <a-button
        type="primary"
        :loading="loading"
        @click="handleSubmit"
      >
        确认
      </a-button>
    </div>
  </a-drawer>
</template>

<script>
import { getDoctorHonorList, setDoctorHonor } from '@/api/doctor/doctor'

export default {
  name: 'CreateDoctorHonorForm',
  data() {
    return {
      open: false,
      loading: false,
      form: {}, // 修复：移除 this.$form.createForm(this)，改为空对象
      doctorInfo: {}, // 医师基础信息
      honorList: [], // 待提交的荣誉列表
      validateStatus: 'success',
      helpMsg: ''
    }
  },
  methods: {
    /**
     * 转换荣誉类型为中文名称（兼容数字/字符串类型）
     */
    getHonorTypeName(type) {
      // 先统一转为数字，避免类型不匹配
      const typeNum = Number(type)
      const typeMap = {
        1: '国家级',
        2: '省级',
        3: '市级'
      }
      return typeMap[typeNum] || '未知类型'
    },

    /**
     * 加载医师已有的荣誉列表（核心修改：正确传递 doctorId + 修复类型回显）
     */
    loadDoctorHonor(doctorId) {
      // 关键：把 doctorId 封装到 query 对象中传递给接口
      getDoctorHonorList({ doctorId: doctorId })
        .then((res) => {
          console.log('荣誉列表接口返回：', res) // 调试用，查看参数是否传递成功
          if (res.success) {
            // 格式化数据，初始化错误状态 + 修复类型回显（统一转为字符串，适配下拉框v-model）
            this.honorList = (res.data || []).map(item => ({
              ...item,
              // 关键修复：把数字类型的 honorType 转为字符串，匹配下拉框的 value（"1"/"2"/"3"）
              honorType: item.honorType ? String(item.honorType) : '',
              nameError: '',
              typeError: ''
            }))
            // 同步到 doctorInfo 中，用于页面展示当前荣誉
            this.doctorInfo.honorList = [...this.honorList]
          } else {
            this.$message.error('加载荣誉列表失败：' + res.message)
          }
        })
        .catch((err) => {
          console.error('加载荣誉列表异常：', err) // 调试用
          this.$message.error('加载荣誉列表异常，请稍后重试')
        })
    },

    /**
     * 打开荣誉设置抽屉
     */
    handleDoctorHonor(record) {
      this.doctorInfo = { ...record }
      this.validateStatus = 'success'
      this.helpMsg = ''
      // 加载该医师已有的荣誉数据
      this.loadDoctorHonor(record.id)
      this.open = true
    },

    /**
     * 关闭抽屉并重置状态
     */
    handleCancel() {
      this.open = false
      this.doctorInfo = {}
      this.honorList = []
      this.validateStatus = 'success'
      this.helpMsg = ''
    },

    /**
     * 添加新的荣誉项
     */
    addHonorItem() {
      this.honorList.push({
        honorName: '',
        honorType: '',
        nameError: '',
        typeError: ''
      })
    },

    /**
     * 删除指定索引的荣誉项
     */
    removeHonorItem(index) {
      this.honorList.splice(index, 1)
      // 删除后重新校验整体表单
      this.validateForm()
    },

    /**
     * 校验单个荣誉项
     */
    validateHonorItem(item) {
      item.nameError = ''
      item.typeError = ''

      // 校验荣誉名称
      if (!item.honorName || item.honorName.trim() === '') {
        item.nameError = '荣誉名称不能为空'
      }

      // 校验荣誉类型
      if (!item.honorType) {
        item.typeError = '请选择荣誉类型'
      }
    },

    /**
     * 整体表单校验
     */
    validateForm() {
      let isValid = true

      // 校验所有荣誉项
      this.honorList.forEach(item => {
        this.validateHonorItem(item)
        if (item.nameError || item.typeError) {
          isValid = false
        }
      })

      // 校验是否有荣誉项
      if (this.honorList.length === 0) {
        this.validateStatus = 'warning'
        this.helpMsg = '未添加任何荣誉项'
        isValid = false
      } else if (!isValid) {
        this.validateStatus = 'error'
        this.helpMsg = '部分荣誉项填写不完整，请检查'
      } else {
        this.validateStatus = 'success'
        this.helpMsg = ''
      }

      return isValid
    },

    /**
     * 提交保存荣誉信息
     */
    handleSubmit() {
      // 先执行整体校验
      if (!this.validateForm()) {
        this.$message.error(this.helpMsg || '荣誉信息填写不完整，请检查')
        return
      }

      this.loading = true
      // 构造提交参数
      const submitData = {
        doctorId: this.doctorInfo.id,
        honorList: this.honorList.map(item => ({
          honorName: item.honorName.trim(),
          honorType: parseInt(item.honorType) // 确保类型为数字
        }))
      }

      // 调用保存接口
      setDoctorHonor(submitData)
        .then((res) => {
          if (res.success) {
            this.$message.success('名医荣誉设置成功')
            this.open = false
            // 通知父组件刷新列表
            this.$emit('ok')
          } else {
            this.$message.error(`名医荣誉设置失败：${res.message}`)
          }
        })
        .catch(() => {
          this.$message.error('名医荣誉设置异常，请稍后重试')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.honor-list {
  margin-bottom: 16px;
}

.honor-item {
  margin-bottom: 8px;
  padding: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.operate-col {
  text-align: center;
}

.add-honor-btn {
  width: 100%;
}

.error-tip {
  color: #f5222d;
  font-size: 12px;
  margin-top: 4px;
}

.current-honor {
  padding: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
}

.current-honor-item {
  margin-bottom: 4px;
  line-height: 1.5;
}

.empty-tip {
  color: #999;
  padding: 8px;
}
</style>